import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Users, CalendarDays, Bell, MessageSquareHeart, ArrowRight, Star } from "lucide-react";
import { Logo } from "@/components/Logo";
import FooterContact from "@/components/FooterContact";
import ThemeToggle from "@/components/ThemeToggle";
import { useContact } from "@/components/ContactModal";
import SEO from "@/components/SEO";
import PriceCalculator from "@/components/PriceCalculator";
import { TrendBadge } from "@/components/TrendBadge";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxzYWxvbnxlbnwwfHx8fDE3NzgxMTE2MTJ8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: CalendarDays, title: "Online booking", text: "Kunder booker selv, døgnet rundt. Ingen telefonjogging." },
  { icon: Users, title: "Kunderegister (CRM)", text: "Alle kontakter, notater og besøk på ett sted." },
  { icon: Bell, title: "Påminnelser", text: "Hold oversikt over kommende avtaler og kunder som trenger oppfølging." },
  { icon: Sparkles, title: "AI oppfølging", text: "Ferdigskrevne norske meldinger til takk og tilbud." },
  { icon: MessageSquareHeart, title: "Faste kunder", text: "Vi minner deg om inaktive kunder så du får dem tilbake." },
];

const plans = [
  {
    name: "Demo",
    price: "0",
    period: "for alltid",
    desc: "Prøv ClientFlow med en fiktiv demo-bedrift.",
    features: [
      "Fiktiv demo-bedrift",
      "Offentlig bookingside",
      "Kalender",
      "Kundeliste med demodata",
      "Åpningstider",
      "Stengte dager / ferie",
      "Bookinglenke og QR-kode",
      "Begrenset bruk",
      "Ikke for ekte drift",
    ],
    cta: "Prøv demo",
    showVat: false,
  },
  {
    name: "Basic",
    price: "199",
    period: "per måned · eks. mva",
    desc: "For små salonger og enkeltpersoner som vil ha enkel booking og kundeoversikt.",
    features: [
      "Ubegrenset bookinger",
      "1 bruker / leder",
      "Offentlig bookingside",
      "Kalender",
      "Kundeliste",
      "Åpningstider",
      "Stengte dager / ferie",
      "Bookingbekreftelse på e-post",
      "Automatisk påminnelse 24 timer før",
      "Endre eller avbestill via lenke",
      "Bookinglenke og QR-kode",
      "Eksport av kunder og bookinger",
      "Enkel support",
    ],
    cta: "Kontakt oss",
    showVat: true,
  },
  {
    name: "Pro",
    price: "399",
    period: "per måned · eks. mva",
    desc: "For salonger med flere ansatte og behov for mer oversikt.",
    features: [
      "Alt i Basic",
      "Flere ansatte / brukere",
      "Personal- og teamoversikt",
      "Kundeoversikt, historikk og notater",
      "Kunder å følge opp",
      "Kalenderfeed (iCal) til Apple/Google/Outlook",
      "Kundetagger og segmenter",
      "AI-genererte oppfølgingsmeldinger (norsk)",
      "Sist kontaktet på kundekort",
      "Prioritert support",
      "Hjelp med oppsett",
      "+99 kr/mnd per ekstra ansatt",
    ],
    cta: "Kontakt oss",
    featured: true,
    showVat: true,
  },
  {
    name: "Premium",
    price: "599",
    period: "per måned · eks. mva",
    desc: "For salonger som vil ha tett oppfølging og personlig hjelp.",
    features: [
      "Alt i Pro",
      "Automatisk «Takk for besøket»-mail",
      "Anmeldelsesoppfølging (Google Reviews-lenke)",
      "Detaljerte rapporter (30 & 90 dager)",
      "PDF-eksport av rapporter",
      "Månedsrapport på e-post",
      "Kundeimport fra CSV-fil",
      "Personlig onboarding-gjennomgang",
      "Ekstra prioritert support",
      "Direkte kontakt med ClientFlow-team",
      "+99 kr/mnd per ekstra ansatt",
    ],
    cta: "Kontakt oss",
    showVat: true,
  },
];

export default function Landing() {
  const { open: openContact } = useContact();
  // Real-data week stats for the hero illustration card. Falls back to
  // "—" / "Ingen sammenligning ennå" when the public endpoint isn't ready;
  // the marketing page must never crash on a transient backend hiccup.
  const [weekStats, setWeekStats] = useState({
    this_week: null,
    label: "",
    ready: false,
  });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/public/landing/stats");
        if (!alive) return;
        setWeekStats({
          this_week: typeof res.data?.this_week === "number" ? res.data.this_week : 0,
          label: res.data?.label || "",
          ready: true,
        });
      } catch {
        if (!alive) return;
        setWeekStats({ this_week: null, label: "", ready: true });
      }
    })();
    return () => { alive = false; };
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="ClientFlow — Bookingsystem & CRM for salonger og småbedrifter i Norge"
        description="Smart bookingsystem og CRM for salonger, frisører, behandlere og servicebedrifter. Online timebestilling, kundekartotek, kalender og påminnelser — bygget for norske småbedrifter."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Hva er ClientFlow?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "ClientFlow er et norsk bookingsystem og CRM laget for salonger, frisører, behandlere og servicebedrifter. Du får online timebestilling, kundekartotek, kalender og påminnelser på ett sted.",
              },
            },
            {
              "@type": "Question",
              name: "Hvor mye koster ClientFlow?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "ClientFlow har tre planer: Basic 199 kr/mnd, Pro 399 kr/mnd og Premium 599 kr/mnd (alle priser eks. mva). Det er også en gratis demo for å prøve tjenesten.",
              },
            },
            {
              "@type": "Question",
              name: "Kan kundene mine booke time selv?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja. Hver bedrift får en offentlig bookingside og QR-kode kundene kan bruke for å bestille time selv — døgnet rundt.",
              },
            },
            {
              "@type": "Question",
              name: "Passer ClientFlow for små bedrifter?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja. ClientFlow er laget for små norske servicebedrifter — frisørsalonger, neglesalonger, behandlere, terapeuter og enkeltpersonforetak.",
              },
            },
          ],
        }}
      />
      {/* Nav */}
      <header className="sticky top-0 z-40 glass-nav border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="landing-logo" aria-label="ClientFlow">
            <Logo className="h-8 sm:h-9 w-auto max-h-10" />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <a href="#funksjoner" className="text-foreground/70 hover:text-foreground">Funksjoner</a>
            <a href="#priser" className="text-foreground/70 hover:text-foreground">Priser</a>
            <Link to="/book/nordlys-frisor" className="text-foreground/70 hover:text-foreground" data-testid="demo-booking-link">Bookingflyt for kunder</Link>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/logg-inn" className="hidden sm:block">
              <Button variant="ghost" className="rounded-xl" data-testid="nav-login-btn">Logg inn</Button>
            </Link>
            <Button
              onClick={() => openContact({ source: "landing-nav" })}
              className="rounded-xl bg-primary hover:bg-primary/90"
              data-testid="nav-book-demo-btn"
            >
              Book demo
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden hero-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="anim-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold tracking-wide">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
              Laget for norske småbedrifter
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05]">
              Flere faste kunder.
              <br />
              <span className="text-primary">Mindre manuelt arbeid.</span>
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl">
              ClientFlow hjelper små salonger, behandlere og servicebedrifter med booking,
              kundeoversikt og enklere kundeoppfølging — slik at flere kunder kommer tilbake.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                onClick={() => openContact({ source: "landing-hero" })}
                className="rounded-xl bg-primary hover:bg-primary/90 w-full sm:w-auto"
                data-testid="hero-cta-book-demo"
              >
                Book demo
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
              </Button>
              <Link to="/logg-inn">
                <Button variant="outline" size="lg" className="rounded-xl w-full sm:w-auto" data-testid="hero-cta-logg-inn">
                  Logg inn
                </Button>
              </Link>
            </div>
            <Link
              to="/book/nordlys-frisor"
              className="mt-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              data-testid="hero-link-bookingflyt"
            >
              Se bookingflyt for kunder
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Link>
            <p className="mt-6 text-xs text-muted-foreground max-w-xl">
              <span className="font-semibold text-foreground/80">Book demo</span> = personlig gjennomgang.{" "}
              <span className="font-semibold text-foreground/80">Logg inn</span> = for deg som allerede har tilgang til ClientFlow.{" "}
              <span className="font-semibold text-foreground/80">Bookingflyt</span> = det kundene dine ser når de booker.
            </p>
            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-current text-foreground" strokeWidth={0} />
                <span>Laget for norske småbedrifter</span>
              </div>
              <span>•</span>
              <span>Enkelt å komme i gang</span>
            </div>
          </div>

          <div className="relative lg:h-[560px] anim-in">
            <div className="absolute inset-0 bg-gradient-to-br from-accent/40 to-transparent rounded-3xl" />
            <img
              src={HERO_IMG}
              alt="Salong"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="relative w-full h-[360px] sm:h-[440px] lg:h-full object-cover rounded-3xl shadow-lg"
            />
            <div
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl p-4 sm:p-5 border border-border dark:border-transparent max-w-[280px]"
              data-testid="hero-illustration-next-booking"
              aria-hidden="true"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-emerald-100 flex items-center justify-center text-primary dark:text-emerald-700">
                  <Bell className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="text-sm leading-snug">
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground dark:text-gray-500">
                    Neste booking
                  </p>
                  <p className="font-semibold dark:text-gray-900">kl. 14:00 · Kari Nordmann</p>
                  <p className="text-muted-foreground dark:text-gray-600 text-xs">Klipp</p>
                </div>
              </div>
            </div>
            <div
              className="absolute -top-2 -right-1 sm:-top-4 sm:-right-6 bg-card rounded-2xl shadow-xl p-3 sm:p-4 border border-border max-w-[150px] sm:max-w-[260px]"
              data-testid="hero-illustration-week-stat"
              aria-hidden="true"
            >
              <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-widest font-semibold leading-tight">Denne uken</div>
              <div
                className="mt-1 font-heading text-lg sm:text-2xl font-bold leading-tight"
                data-testid="hero-week-count"
              >
                {weekStats.this_week == null ? "—" : `${weekStats.this_week} ${weekStats.this_week === 1 ? "booking" : "bookinger"}`}
              </div>
              <div
                className="text-[10px] sm:text-xs text-foreground/60 leading-tight mt-0.5"
                data-testid="hero-week-label"
              >
                {weekStats.label
                  ? <TrendBadge label={weekStats.label} className="text-[10px] sm:text-xs" />
                  : (weekStats.ready ? "denne uken" : "")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="funksjoner" className="py-20 lg:py-28 bg-muted/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest font-semibold text-primary">Funksjoner</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">
              Booking, kunder og oppfølging — uten styr.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-card rounded-2xl border border-border p-6 card-hover"
                data-testid={`feature-${f.title}`}
              >
                <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center text-primary">
                  <f.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-xl">{f.title}</h3>
                <p className="mt-2 text-muted-foreground">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="priser" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-widest font-semibold text-primary">Priser</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">
              Enkel og ærlig prising
            </h2>
            <p className="mt-3 text-muted-foreground">Velg pakken som passer deg. Ingen bindingstid.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {plans.map((p) => (
              <div
                key={p.name}
                className={`rounded-2xl border p-8 flex flex-col card-hover ${
                  p.featured ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"
                }`}
                data-testid={`plan-${p.name.toLowerCase()}`}
              >
                {p.featured && (
                  <div className="inline-block px-3 py-1 rounded-full bg-primary-foreground/15 text-xs font-semibold uppercase tracking-widest mb-4 w-fit">
                    Mest populær
                  </div>
                )}
                <h3 className="font-heading text-2xl font-semibold">{p.name}</h3>
                <p className={`mt-1 text-sm ${p.featured ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{p.desc}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-heading text-5xl font-bold">{p.price}</span>
                  <span className={`${p.featured ? "text-primary-foreground/80" : "text-muted-foreground"}`}>kr</span>
                </div>
                <p className={`text-sm ${p.featured ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{p.period}</p>
                <ul className="mt-8 space-y-3 text-sm">
                  {p.features.map((feat) => (
                    <li key={feat} className="flex gap-2">
                      <Check className={`w-4 h-4 mt-0.5 ${p.featured ? "" : "text-primary"}`} strokeWidth={2} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => {
                    if (p.name === "Demo") {
                      window.location.href = "/logg-inn";
                    } else {
                      openContact({ source: `landing-plan-${p.name.toLowerCase()}` });
                    }
                  }}
                  className={`w-full rounded-xl mt-8 ${
                    p.featured
                      ? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground"
                  }`}
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                >
                  {p.cta}
                </Button>
              </div>
            ))}
          </div>
          <PriceCalculator />
          <p
            className="mt-10 text-center text-sm text-muted-foreground max-w-2xl mx-auto"
            data-testid="pricing-roadmap-note"
          >
            Stripe-betaling fra kunder via bookinglenken er under utvikling.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl bg-primary text-primary-foreground p-10 lg:p-16 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Klar for flere faste kunder?
            </h2>
            <p className="mt-4 text-primary-foreground/80 max-w-xl mx-auto">
              ClientFlow er for tiden i privat demo. Book en demo-gjennomgang så åpner vi en konto for deg.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
              <Button
                size="lg"
                onClick={() => openContact({ source: "landing-final" })}
                className="rounded-xl bg-primary-foreground text-primary hover:bg-primary-foreground/90 w-full sm:w-auto"
                data-testid="final-cta-book-demo"
              >
                Book demo
              </Button>
              <Link to="/logg-inn">
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-xl w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  data-testid="final-cta-logg-inn"
                >
                  Logg inn
                </Button>
              </Link>
            </div>
            <Link
              to="/book/nordlys-frisor"
              className="mt-5 inline-flex items-center gap-1 text-sm text-primary-foreground/80 hover:text-primary-foreground underline-offset-4 hover:underline"
              data-testid="final-link-bookingflyt"
            >
              Se bookingflyt for kunder
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Logo className="h-6 w-auto" />
            <span className="ml-2">© 2026 ClientFlow</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/vilkar" className="hover:text-foreground" data-testid="footer-link-vilkar">Vilkår</Link>
            <Link to="/personvern" className="hover:text-foreground" data-testid="footer-link-personvern">Personvern</Link>
            <FooterContact />
          </div>
        </div>
      </footer>
    </div>
  );
}
